import React from 'react';
import { Box, Flex, Spinner, Skeleton } from '@chakra-ui/react';
import { MotionBox, useMotionPrefs, EASE } from './motion';

/**
 * The top of the thread while older messages are on their way.
 *
 * ★ IT ALWAYS OCCUPIES THE SAME HEIGHT, AND THAT IS THE WHOLE POINT.
 * Loading indicators are normally mounted when a fetch starts and unmounted
 * when it ends. Above a scroll position being held in place, that is a bug: the
 * strip appears, everything below it moves down, the reader sees a jump; the
 * page arrives, the strip disappears, and everything moves back up. The scroll
 * compensation cannot correct for it either, because the geometry changes
 * twice, on either side of the moment the anchor is applied.
 *
 * So the slot is reserved whenever there is more history, and only its CONTENTS
 * fade in and out. The scroll container's height never changes on account of
 * this component, and the anchor arithmetic below it stays exact.
 *
 * ★ AND IT IS DELIBERATELY SMALL. A full-width page loader over the thread
 * would hide the very messages the reader is reading — they scrolled up to look
 * at something. Two muted bubble shapes and a spinner say "more is coming"
 * without taking the conversation away.
 */

/** The reserved height. Constant, and the only number this file owns. */
const SLOT_PX = 56;

function GhostBubble({ side = 'left', w }) {
	return (
		<Flex justify={side === 'left' ? 'flex-start' : 'flex-end'} px="4" py="1">
			<Skeleton
				h="14px"
				w={w}
				borderRadius="md"
				startColor="wa.chip"
				endColor="wa.header"
				speed={1.2}
			/>
		</Flex>
	);
}

export default function HistoryLoader({ loading, hasMore }) {
	const { dur, reduced } = useMotionPrefs();

	// Nothing above to fetch: no slot, no reserved space, no empty gap at the
	// top of a fully-loaded thread.
	if (!hasMore) return null;

	return (
		<Box h={`${SLOT_PX}px`} flexShrink={0} aria-hidden={!loading} position="relative">
			<MotionBox
				position="absolute"
				inset="0"
				display="flex"
				flexDirection="column"
				justifyContent="center"
				initial={false}
				animate={{ opacity: loading ? 1 : 0 }}
				transition={{ duration: reduced ? 0 : dur(220), ease: EASE }}
				// Invisible is not the same as absent for a pointer; while idle the
				// slot must not eat a click meant for the message under it.
				style={{ pointerEvents: 'none' }}
			>
				<GhostBubble side="left" w="42%" />
				<GhostBubble side="right" w="30%" />
				<Flex justify="center" mt="1">
					<Spinner size="xs" color="wa.inkMuted" thickness="2px" speed="0.9s" />
				</Flex>
			</MotionBox>
		</Box>
	);
}

export { SLOT_PX };
